import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert as MuiAlert,
  List,
  ListItem,
  ListItemText,
  Alert,
  CardActions,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Timeline as TimelineIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Folder as FolderIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import Grid from '@mui/material/GridLegacy';
import { RootState, AppDispatch } from "../store";
import { fetchCaseById, updateCase, updateCaseStatus } from "../store/slices/casesSlice";
import { fetchClients } from "../store/slices/clientsSlice";
import { fetchBills, createBill, sendBillAction, markBillAsPaidAction } from "../store/slices/billingSlice";
import { fetchCaseEvents, addCaseEvent, updateCaseEvent, deleteCaseEvent } from "../store/slices/caseEventsSlice";
import usersService from "../services/usersService";
import { Bill, BillCreateInput } from "../services/billingService";
import { Case } from "../services/casesService";
import caseFilesService from "../services/caseFilesService";
import StatusBadge from "../components/common/StatusBadge";
import RichTextEditor from "../components/common/RichTextEditor";
import EventDescriptionDisplay from "../components/common/EventDescriptionDisplay";
import { useConfirm } from "../contexts/ConfirmDialogContext";
import { generatePublicAccessLink } from "../utils/passwordGenerator";
import { getCaseProgressFromStatus } from "../utils/caseProgress";

/**
 * CaseDetails Component
 * 
 * BILLING SYSTEM UPDATE:
 * The backend now handles all activity log charge calculations automatically.
 * When an activity log is added:
 * 1. Backend calculates charges based on person (supervisor vs lawyer) and their rate
 * 2. Backend returns case_charges with total_charges in the response
 * 3. Frontend fetches updated case to sync total_charges
 * 
 * Frontend no longer calculates charges - backend does the heavy lifting.
 * This ensures consistency and prevents calculation mismatches.
 */

const CaseDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { clients } = useSelector((state: RootState) => state.clients);
  const { currentCase, loading } = useSelector(
    (state: RootState) => state.cases,
  );
  const { bills } = useSelector(
    (state: RootState) => state.bills,
  );

  const { logs } = useSelector(
    (state: RootState) => state.caseEvents,
  );
  const { user } = useSelector(
    (state: RootState) => state.auth,
  );
  const [users, setUsers] = useState<any[]>([]);
  const [openLogDialog, setOpenLogDialog] = useState(false);
  const [openBillDialog, setOpenBillDialog] = useState(false);
  const [newLog, setNewLog] = useState({
    title: "",
    activity_description: "",
    lawyer_name: "",
    time_spent: "",
    status: "pending",
  });
  const [editLogDialogOpen, setEditLogDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<any>(null);
  const [editLogData, setEditLogData] = useState({
    title: "",
    activity_description: "",
    lawyer_name: "",
    time_spent: "",
    status: "pending",
  });
  const [deleteLogDialogOpen, setDeleteLogDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [confirmUploadDialog, setConfirmUploadDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    customer_id: "",
    type: "",
    subject: "",
    billing_type: "hourly",
    currency: "USD",
    priority: "medium",
    supervisor: "",
    supervisor_fee: "",
    lawyers_assigned: [] as { user_id: string; fee: string }[],
  });
  const [editFormErrors, setEditFormErrors] = useState({
    customer_id: "",
    type: "",
    subject: "",
  });
  const [editDialogTab, setEditDialogTab] = useState(0);
  const [mainTab, setMainTab] = useState(0);

  useEffect(() => {
    if (id && user?.user_id) {
      const userId = user.user_id;
      dispatch(fetchCaseById({ orderId: id, userId }));
      dispatch(fetchClients(userId));
      dispatch(fetchBills(userId.toString()));
      dispatch(fetchCaseEvents(parseInt(id)));
      loadUsers();
    }
  }, [id, dispatch, user]);

  useEffect(() => {
    // Cases with updated total_charges will be fetched from backend after activity log changes
    // No manual calculation needed anymore - backend handles charging calculations
    
    // Parse files from currentCase when it changes
    if (currentCase) {
      const parsedFiles = caseFilesService.parseFilesFromCase(currentCase);
      setFiles(parsedFiles);
    }
  }, [currentCase, logs]);
  
  const loadUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData.data);
    } catch (err: any) {
      console.error("Failed to load users:", err);
    }
  };

  const handleAddEvent = async () => {
    // Check if time_spent is required (not fixed billing type)
    const isTimeSpentRequired = currentCase?.billing_type !== 'fixed';
    
    if (
      !newLog.title ||
      !newLog.activity_description ||
      !newLog.lawyer_name ||
      (isTimeSpentRequired && !newLog.time_spent)
    ) {
      setSnackbar({
        open: true,
        message: isTimeSpentRequired
          ? t('validation.fillAllRequiredFields', 'Please fill all required fields')
          : t('cases.fillActivityTitleDescriptionAndLawyer', 'Please fill activity title, description and lawyer name'),
        severity: "error",
      });
      return;
    }

    // Find lawyer ID from the selected lawyer name
    const selectedLawyer = users.find(user => user.full_name === newLog.lawyer_name);
    const lawyer_id = selectedLawyer?.user_id?.toString() || null;

    const logData = {
      case_id: parseInt(id!),
      title: newLog.title,
      lawyer_id: lawyer_id,
      time_spent: newLog.time_spent ? parseFloat(newLog.time_spent) : 0,
      description: newLog.activity_description,
      status: newLog.status,
    };

    try {
      const result = await dispatch(addCaseEvent(logData));
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('cases.failedToAddEvent', 'Failed to add event'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('cases.eventAddedSuccess', 'Event added successfully'),
          severity: "success",
        });
        setNewLog({ title: "", activity_description: "", lawyer_name: "", time_spent: "", status: "pending" });
        setOpenLogDialog(false);
        // Both dispatch calls happen in the thunk now - fetch logs and refresh case
        dispatch(fetchCaseEvents(parseInt(id!)));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('cases.failedToAddEvent', 'Failed to add event'),
        severity: "error",
      });
    }
  };

  const openEditLogDialog = (log: any) => {
    setEditingLog(log);
    setEditLogData({
      title: log.title || "",
      activity_description: log.description || "",
      lawyer_name: log?.lawyer?.full_name || "",
      time_spent:
        log.time_spent !== undefined && log.time_spent !== null
          ? String(log.time_spent)
          : "",
      status: log.status || "pending",
    });
    setEditLogDialogOpen(true);
  };

  const handleUpdateLog = async () => {
    if (!editingLog) return;

    const isTimeSpentRequired = currentCase?.billing_type !== 'fixed';

    if (
      !editLogData.title ||
      !editLogData.activity_description ||
      !editLogData.lawyer_name ||
      (isTimeSpentRequired && !editLogData.time_spent)
    ) {
      setSnackbar({
        open: true,
        message: isTimeSpentRequired
          ? t('validation.fillAllRequiredFields', 'Please fill all required fields')
          : t('cases.fillActivityTitleDescriptionAndLawyer', 'Please fill activity title, description and lawyer name'),
        severity: "error",
      });
      return;
    }

    const selectedLawyer = users.find(user => user.full_name === editLogData.lawyer_name);
    const lawyer_id = selectedLawyer?.user_id?.toString() || null;

    const logData = {
      id: editingLog.id,
      case_id: parseInt(id!),
      title: editLogData.title,
      lawyer_id,
      time_spent: editLogData.time_spent ? parseFloat(editLogData.time_spent) : 0,
      description: editLogData.activity_description,
      status: editLogData.status,
    };

    try {
      const result = await dispatch(updateCaseEvent(logData));
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('cases.failedToUpdateEvent', 'Failed to update event'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('cases.eventUpdatedSuccess', 'Event updated successfully'),
          severity: "success",
        });
        setEditLogDialogOpen(false);
        setEditingLog(null);
        dispatch(fetchCaseEvents(parseInt(id!)));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('cases.failedToUpdateEvent', 'Failed to update event'),
        severity: "error",
      });
    }
  };

  const openDeleteLogDialog = (log: any) => {
    setLogToDelete(log);
    setDeleteLogDialogOpen(true);
  };

  const handleDeleteLog = async () => {
    if (!logToDelete) return;

    try {
      const result = await dispatch(
        deleteCaseEvent({ id: logToDelete.id, case_id: parseInt(id!) }),
      );
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('cases.failedToDeleteEvent', 'Failed to delete event'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('cases.eventDeletedSuccess', 'Event deleted successfully'),
          severity: "success",
        });
        dispatch(fetchCaseEvents(parseInt(id!)));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('cases.failedToDeleteEvent', 'Failed to delete event'),
        severity: "error",
      });
    } finally {
      setDeleteLogDialogOpen(false);
      setLogToDelete(null);
    }
  };

  const handleCreateBill = async () => {
    if (!currentCase?.customer_id) {
      setSnackbar({
        open: true,
        message: t('billing.noClientAssociated', 'No client associated with this case'),
        severity: "error",
      });
      return;
    }

    if (logs?.length === 0) {
      setSnackbar({
        open: true,
        message: t('billing.noEventsToBill', 'No events to bill'),
        severity: "error",
      });
      return;
    }

    let totalAmount = 0;
    let billItems = [];

    if (currentCase?.billing_type === "fixed") {
      // For fixed billing: total_charges = fixed_fee
      totalAmount = currentCase?.fixed_fee || currentCase?.total_charges || 0;
      billItems = [{
        description: t('cases.fixedFeeFor', 'Fixed Fee for {{subject}}', { subject: currentCase.subject }),
        quantity: 1,
        unit_price: totalAmount,
        total: totalAmount,
      }];
    } else {
      // For hourly billing: total_charges = supervisor fee x hours + lawyer fees x hours
      // Group activity logs by lawyer to calculate total hours per person
      const hoursByLawyer = logs?.reduce((acc: any, log: any) => {
        // Handle different lawyer data structures
        let lawyerName = 'Unknown';
        if (log?.lawyer?.full_name) {
          lawyerName = log.lawyer.full_name;
        } else if (log?.lawyer_name) {
          lawyerName = log.lawyer_name;
        } else if (typeof log?.lawyer === 'string') {
          lawyerName = log.lawyer;
        }
        
        if (!acc[lawyerName]) {
          acc[lawyerName] = 0;
        }
        
        // Ensure time_spent is a valid number
        const timeSpent = parseFloat(String(log.time_spent)) || 0;
        acc[lawyerName] += timeSpent;
        return acc;
      }, {});

      billItems = [];
      
      // Add supervisor charges if supervisor is assigned
      if (currentCase?.supervisor && currentCase?.supervisor_fee > 0) {
        // Handle different supervisor data structures
        const supervisorName = currentCase.supervisor?.full_name || currentCase.supervisor?.name || currentCase.supervisor;
        const supervisorHours = parseFloat(String(hoursByLawyer[supervisorName])) || 0;
        if (supervisorHours > 0) {
          const supervisorFee = parseFloat(String(currentCase.supervisor_fee)) || 0;
          const supervisorCharge = supervisorHours * supervisorFee;
          totalAmount += supervisorCharge;
          billItems.push({
            description: t('cases.supervisorChargeDescription', 'Supervisor ({{name}}) - {{hours}} hours', { name: supervisorName, hours: supervisorHours }),
            quantity: supervisorHours,
            unit_price: supervisorFee,
            total: supervisorCharge,
          });
        }
      }

      // Add lawyer charges
      // Parse lawyers_assigned to get user_id: fee pairs
      const lawyerFees: { [key: string]: number } = {};
      if (currentCase?.lawyers_assigned && typeof currentCase.lawyers_assigned === 'string') {
        currentCase.lawyers_assigned.split(',').forEach(entry => {
          const [userId, fee] = entry.trim().split(':').map(s => s.trim());
          if (userId && fee) {
            lawyerFees[userId] = parseFloat(fee) || 0;
          }
        });
      }

      Object.entries(hoursByLawyer).forEach(([lawyerName, hours]) => {
        // Skip if this is the supervisor (already handled)
        const supervisorName = currentCase?.supervisor?.full_name || currentCase?.supervisor?.name || currentCase?.supervisor;
        if (supervisorName === lawyerName) return;
        
        // Find the lawyer's user_id from lawyers_list to get their fee
        const lawyerInfo = currentCase?.lawyers_list?.find((lawyer: any) => lawyer.full_name === lawyerName);
        const lawyerRate = lawyerInfo ? lawyerFees[lawyerInfo.user_id] || 250 : 250;
        
        const hoursNum = parseFloat(String(hours)) || 0;
        const lawyerCharge = hoursNum * lawyerRate;
        totalAmount += lawyerCharge;
        billItems.push({
          description: t('cases.lawyerChargeDescription', '{{name}} - {{hours}} hours @ {{currency}}{{rate}}/h', { name: lawyerName, hours: hoursNum, currency: getCurrencySymbol(currentCase?.currency || "USD"), rate: lawyerRate }),
          quantity: hoursNum,
          unit_price: lawyerRate,
          total: lawyerCharge,
        });
      });
    }

    const billData: BillCreateInput = {
      user_id: user?.user_id || "",
      customer_id: currentCase.customer_id,
      case_id: parseInt(id!),
      items: billItems,
      subtotal: totalAmount,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      total: totalAmount,
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      issued_date: new Date().toISOString().split('T')[0],
      currency: currentCase?.currency || "USD",
    };

    try {
      const result = await dispatch(createBill(billData));
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('billing.failedToCreateBill', 'Failed to create bill'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('billing.billCreated', 'Bill created successfully'),
          severity: "success",
        });
        setOpenBillDialog(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('billing.failedToCreateBill', 'Failed to create bill'),
        severity: "error",
      });
    }
    dispatch(fetchBills(user?.user_id.toString() || ""));
  };

  const handleSendBill = async (billId: number) => {
    try {
      const result = await dispatch(sendBillAction(billId));
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('billing.failedToSendBill', 'Failed to send bill'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('billing.billSentSuccess', 'Bill sent to client successfully'),
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('billing.failedToSendBill', 'Failed to send bill'),
        severity: "error",
      });
    }
  };

  const handleMarkAsPaid = async (billId: number) => {
    try {
      const result = await dispatch(markBillAsPaidAction({
        billId,
        paymentMethod: "bank_transfer",
        paymentDate: new Date().toISOString().split('T')[0],
      }));
      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('billing.failedToMarkBillAsPaid', 'Failed to mark bill as paid'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('billing.billMarkedAsPaidSuccess', 'Bill marked as paid successfully'),
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('billing.failedToMarkBillAsPaid', 'Failed to mark bill as paid'),
        severity: "error",
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setSelectedFiles(files);
    setConfirmUploadDialog(true);
  };

  const confirmFileUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    setConfirmUploadDialog(false);

    try {
      const result = await caseFilesService.uploadFiles(parseInt(id!), selectedFiles);

      if (result.status) {
        setSnackbar({
          open: true,
          message: t('documents.filesUploadedSuccess', 'Files uploaded successfully'),
          severity: "success",
        });
        // Refresh case data to get updated files
        dispatch(fetchCaseById({ orderId: id!, userId: user!.user_id }));
      } else {
        setSnackbar({
          open: true,
          message: result.message || t('documents.failedToUploadFiles', 'Failed to upload files'),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('documents.failedToUploadFiles', 'Failed to upload files'),
        severity: "error",
      });
    } finally {
      setUploading(false);
      setSelectedFiles(null);
    }
  };

  const cancelFileUpload = () => {
    setSelectedFiles(null);
    setConfirmUploadDialog(false);
  };

  const handleDeleteFile = async (file: any) => {
    const ok = await confirm(t('documents.confirmDeleteFile', 'Are you sure you want to delete this file?'));
    if (!ok) return;

    try {
      const result = await caseFilesService.deleteCaseFile(parseInt(id!), file.path);

      if (result.status) {
        setSnackbar({
          open: true,
          message: t('documents.fileDeletedSuccess', 'File deleted successfully'),
          severity: "success",
        });
        // Refresh case data to get updated files
        dispatch(fetchCaseById({ orderId: id!, userId: user!.user_id }));
      } else {
        setSnackbar({
          open: true,
          message: result.message || t('documents.failedToDeleteFile', 'Failed to delete file'),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('documents.failedToDeleteFile', 'Failed to delete file'),
        severity: "error",
      });
    }
  };

  const handleViewFile = (file: any) => {
    const fileUrl = caseFilesService.getFileUrl(file);
    window.open(fileUrl, '_blank');
  };

  const handleDownloadFile = async (file: any) => {
    try {
      const fileUrl = caseFilesService.getFileUrl(file);
      
      // Try fetch approach first (better for forcing download)
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }
        
        const blob = await response.blob();
        
        // Create a temporary link element
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = file.original_filename || file.filename || 'download';
        
        // Trigger the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setSnackbar({
          open: true,
          message: t('documents.downloadStarted', 'Download started'),
          severity: "success",
        });
      } catch (fetchError) {
        // Fallback to direct link approach if fetch fails (CORS issues)
        console.warn('Fetch failed, trying fallback approach:', fetchError);
        
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = file.original_filename || file.filename || 'download';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        
        setSnackbar({
          open: true,
          message: t('documents.downloadStartedNewTab', 'Download started (opened in new tab)'),
          severity: "success",
        });
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setSnackbar({
        open: true,
        message: t('documents.failedToDownloadFile', 'Failed to download file'),
        severity: "error",
      });
    }
  };

  const getBillStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      draft: "#64748b",
      sent: "#3b82f6",
      paid: "#22c55e",
    };
    return colors[status] || colors.draft;
  };

  const statusOptions = [
    { value: 'pending', label: t('common.pending', 'Pending'), color: '#f97316' },
    { value: 'in_progress', label: t('tasks.inProgress', 'In Progress'), color: '#3B82F6' },
    { value: 'completed', label: t('common.completed', 'Completed'), color: '#059669' },
    { value: 'closed', label: t('status.closed', 'Closed'), color: '#6B7280' },
    { value: 'archived', label: t('status.archived', 'Archived'), color: '#6B7280'},
    { value: 'on_hold', label: t('status.onHold', 'On Hold'), color: '#F59E0B' },
    { value: 'cancelled', label: t('status.cancelled', 'Cancelled'), color: '#EF4444' }
  ];

  const eventStatusOptions = statusOptions.filter((option) =>
    ['pending', 'in_progress', 'completed'].includes(option.value),
  );

  const handleStatusUpdate = async () => {
    if (!selectedStatus || !currentCase?.id) return;

    try {
      const result = await dispatch(updateCaseStatus({
        user_id: user?.user_id || '',
        id: currentCase.id,
        status: selectedStatus,
      }));

      if ('error' in result || result.payload?.error) {
        setSnackbar({
          open: true,
          message: result.payload?.message || t('cases.failedToUpdateStatus', 'Failed to update status'),
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: t('cases.caseStatusUpdatedSuccess', 'Case status updated successfully'),
          severity: 'success',
        });
        setStatusDialogOpen(false);
        // Refresh case data
        dispatch(fetchCaseById({ orderId: id!, userId: user?.user_id || '' }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('cases.failedToUpdateStatus', 'Failed to update status'),
        severity: 'error',
      });
    }
  };

  const openStatusDialog = () => {
    setSelectedStatus(currentCase?.status || '');
    setStatusDialogOpen(true);
  };

  const openEditDialog = () => {
    if (currentCase) {
      // Parse existing lawyers_assigned string into array if needed
      let lawyersArray: { user_id: string; fee: string }[] = [];
      if (currentCase.lawyers_assigned && typeof currentCase.lawyers_assigned === 'string') {
        lawyersArray = currentCase.lawyers_assigned.split(',').map((entry: any) => {
          const [userId, fee] = entry.trim().split(':').map((s: any) => s.trim());
          return {
            user_id: userId,
            fee: fee || '250'
          };
        });
      }
      
      setEditFormData({
        customer_id: currentCase.customer_id?.toString() || "",
        type: currentCase.type || "",
        subject: currentCase.subject || "",
        billing_type: currentCase.billing_type || "hourly",
        currency: currentCase.currency || "USD",
        priority: currentCase.priority || "medium",
        supervisor: currentCase.supervisor?.user_id || "",
        supervisor_fee: currentCase.supervisor_fee?.toString() || "",
        lawyers_assigned: lawyersArray,
      });
    }
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditFormData({
      customer_id: "",
      type: "",
      subject: "",
      billing_type: "hourly",
      currency: "USD",
      priority: "medium",
      supervisor: "",
      supervisor_fee: "",
      lawyers_assigned: [],
    });
    setEditFormErrors({
      customer_id: "",
      type: "",
      subject: "",
    });
  };

  const handleUpdateCase = async () => {
    // Validate form
    const errors = {
      customer_id: !editFormData.customer_id ? t('validation.clientRequired', 'Client is required') : "",
      type: !editFormData.type ? t('validation.caseTypeRequired', 'Case type is required') : "",
      subject: !editFormData.subject ? t('validation.subjectRequired', 'Subject is required') : "",
    };

    if (errors.customer_id || errors.type || errors.subject) {
      setEditFormErrors(errors);
      return;
    }

    try {
      // Prepare lawyers_assigned string
      const lawyersAssignedString = editFormData.lawyers_assigned
        .map((lawyer: any) => `${lawyer.user_id}:${lawyer.fee}`)
        .join(',');

      const caseData = {
        user_id: user?.user_id || '',
        start_date: currentCase?.start_date,
        end_date: currentCase?.end_date,
        customer_id: parseInt(editFormData.customer_id),
        type: editFormData.type,
        subject: editFormData.subject,
        billing_type: editFormData.billing_type,
        currency: editFormData.currency,
        priority: editFormData.priority,
        supervisor: editFormData.supervisor || null,
        supervisor_fee: editFormData.supervisor_fee ? parseFloat(editFormData.supervisor_fee) : undefined,
        fixed_fee: editFormData.billing_type === 'fixed'
          ? (currentCase?.fixed_fee || currentCase?.total_charges || 0)
          : undefined,
        lawyers_assigned: lawyersAssignedString,
      };

      const result = await dispatch(updateCase({ 
        id: parseInt(id!), 
        ...caseData 
      }));

      if ('error' in result) {
        setSnackbar({
          open: true,
          message: t('cases.failedToUpdateCase', 'Failed to update case'),
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: t('cases.caseUpdated', 'Case updated successfully'),
          severity: "success",
        });
        handleCloseEditDialog();
        // Refresh case data
        dispatch(fetchCaseById({ orderId: id!, userId: user?.user_id || '' }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('cases.failedToUpdateCase', 'Failed to update case'),
        severity: "error",
      });
    }
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      XOF: "CFA",
    };
    return symbols[currency] || "$";
  };


  if (loading || !currentCase) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('cases.loadingCaseDetails', 'Loading case details...')}</Typography>
      </Box>
    );
  }

  const client = clients.find((c) => c.id === currentCase.customer_id);

  // Same stage-based calculation the cases list/kanban use, so the
  // percentage shown here always matches what's shown there for this case.
  const caseProgress = getCaseProgressFromStatus(currentCase.status);
  const priority = (currentCase.priority || "medium").toLowerCase();
  const priorityColor: Record<string, "default" | "warning" | "error"> = {
    low: "default",
    medium: "warning",
    high: "error",
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 380px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <IconButton onClick={() => navigate("/cases")} size="small" sx={{ ml: -1 }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 700 }}>
                  {t("cases.caseNumber", "Case")} #{currentCase.id}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                {currentCase.subject}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <StatusBadge status={currentCase.status} />
                <Chip
                  label={priority}
                  size="small"
                  color={priorityColor[priority] || "default"}
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                />
                {currentCase.type && (
                  <Chip label={currentCase.type} size="small" variant="outlined" />
                )}
                <Chip
                  label={currentCase.billing_type === "fixed" ? t('cases.fixedBilling', 'Fixed Billing') : t('cases.hourlyBilling', 'Hourly Billing')}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>

            <Card variant="outlined" sx={{ minWidth: 240, flexShrink: 0 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {t("dashboard.progress", "Progress")}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {caseProgress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={caseProgress}
                  sx={{ height: 6, borderRadius: 3, mb: 2 }}
                />
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    disabled={!currentCase.enable_public_access || !currentCase.public_access_code}
                    onClick={() => {
                      if (!user?.firm_id || !currentCase.public_access_code) return;
                      const link = generatePublicAccessLink(currentCase.id, user.firm_id, currentCase.public_access_code);
                      window.open(link, "_blank", "noopener,noreferrer");
                    }}
                  >
                    {t("cases.openPortal", "Open Portal")}
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    disabled={!currentCase.enable_public_access || !currentCase.public_access_code}
                    onClick={() => {
                      if (!user?.firm_id || !currentCase.public_access_code) return;
                      const link = generatePublicAccessLink(currentCase.id, user.firm_id, currentCase.public_access_code);
                      navigator.clipboard.writeText(link);
                      setSnackbar({ open: true, message: t('cases.portalLinkCopied', 'Portal link copied'), severity: "success" });
                    }}
                  >
                    {t("cases.copyLink", "Copy Link")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('cases.client', 'Client')}
              </Typography>
              <Typography variant="body1">{client ? client.company : t('cases.unknown', 'Unknown')}</Typography>
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('cases.supervisor', 'Supervisor')}
              </Typography>
              <Typography variant="body1">
                {currentCase.supervisor?.full_name || t('cases.noSupervisorAssigned', 'No supervisor assigned')}
              </Typography>
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('cases.opened', 'Opened')}
              </Typography>
              <Typography variant="body1">
                {currentCase.start_date ? new Date(currentCase.start_date).toLocaleDateString() : "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('cases.estimated', 'Estimated')}
              </Typography>
              <Typography variant="body1">
                {currentCase.end_date ? new Date(currentCase.end_date).toLocaleDateString() : "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                {t('cases.team', 'Team')}
              </Typography>
              {currentCase.lawyers_list && currentCase.lawyers_list.length > 0 ? (
                <AvatarGroup max={5} sx={{ justifyContent: "flex-start", "& .MuiAvatar-root": { width: 32, height: 32, fontSize: "0.75rem" } }}>
                  {currentCase.lawyers_list.map((lawyer: any) => (
                    <Avatar key={lawyer.user_id || lawyer.full_name} sx={{ bgcolor: "primary.main" }}>
                      {(lawyer.full_name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
              ) : (
                <Typography variant="body1">{t('cases.noLawyersAssigned', 'No lawyers assigned')}</Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
            <Button size="small" variant="outlined" onClick={openStatusDialog}>
              {t('cases.updateStatus', 'Update Status')}
            </Button>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEditDialog}>
              {t('cases.editCase', 'Edit Case')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Quick actions */}
      {/* <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewLog({ title: "", activity_description: "", lawyer_name: user?.full_name || "", time_spent: "", status: "pending" });
            setOpenLogDialog(true);
          }}
          disabled={currentCase.status !== "in_progress"}
        >
          {t("cases.newEvent", "New Event")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FolderIcon />}
          onClick={() => setMainTab(1)}
        >
          {t("navigation.documents", "Documents")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ReceiptIcon />}
          onClick={() => setMainTab(2)}
        >
          {t("navigation.billing", "Billing")}
        </Button>
      </Box> */}

      <Tabs
        value={mainTab}
        onChange={(_, value) => setMainTab(value)}
        sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
      >
        <Tab label={t('cases.timeline', 'Timeline')} />
        <Tab label={t('navigation.documents', 'Documents')} />
        <Tab label={t('navigation.billing', 'Billing')} />
      </Tabs>

      {/* Activity Logs */}
      {mainTab === 0 && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <TimelineIcon sx={{ mr: 1 }} />
              {t('cases.events', 'Events')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewLog({ title: "", activity_description: "", lawyer_name: user?.full_name || "", time_spent: "", status: "pending" });
                setOpenLogDialog(true);
              }}
              disabled={currentCase.status !== 'in_progress'}
            >
              {t('cases.newEvent', 'New Event')}
            </Button>
          </Box>

          {logs?.length === 0 ? (
            <Typography color="textSecondary">{t('cases.noEventsYet', 'No events yet')}</Typography>
          ) : (
            <Box>
              {logs?.map((log: any, index: number) => {
                const statusMeta =
                  eventStatusOptions.find(
                    (option) => option.value === (log.status || 'pending'),
                  ) || eventStatusOptions[0];
                const isLast = index === logs.length - 1;
                return (
                  <Box key={log.id} sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: statusMeta.color,
                          mt: 2,
                          flexShrink: 0,
                        }}
                      />
                      {!isLast && (
                        <Box sx={{ flex: 1, width: "2px", bgcolor: "divider", my: 0.5 }} />
                      )}
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{ flex: 1, mb: isLast ? 0 : 2, p: 2, borderRadius: 2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {log.title || log.description}
                          </Typography>
                          <StatusBadge status={log.status} />
                        </Box>
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            aria-label={t('cases.editEventAriaLabel', 'edit event')}
                            onClick={() => openEditLogDialog(log)}
                            disabled={currentCase.status !== 'in_progress'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={t('cases.deleteEventAriaLabel', 'delete event')}
                            onClick={() => openDeleteLogDialog(log)}
                            disabled={currentCase.status !== 'in_progress'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                      <EventDescriptionDisplay content={log.description} sx={{ mt: 1, mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('cases.loggedBy', 'By {{name}}', { name: log?.lawyer?.full_name })} •{" "}
                        {(() => {
                          const date = new Date(log.created_at);
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const year = date.getFullYear();
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${month}-${day}-${year} • ${hours}:${minutes}`;
                        })()} •{" "}
                        {t('cases.hoursLogged', '{{hours}} hours', { hours: log.time_spent })}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Case Files */}
      {mainTab === 1 && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <AttachFileIcon sx={{ mr: 1 }} />
              {t('cases.caseFiles', 'Case Files')}
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading || currentCase.status !== 'in_progress'}
            >
              {t('cases.uploadCaseFile', 'Upload Case File')}
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept="*/*"
              />
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>{t('documents.loadingFiles', 'Loading files...')}</Typography>
            </Box>
          ) : files?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="textSecondary">
                {t('documents.noFilesUploadedYet', 'No files uploaded yet')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('documents.uploadFilesHint', 'Upload case documents, evidence, or other relevant files')}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {files?.map((file) => (
                <Grid item xs={12} sm={6} md={4} key={file.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                          }}
                        >
                          <FileIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {file.original_filename || file.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {caseFilesService.formatFileSize(file.file_size)}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {(() => {
                              const date = new Date(file.created_at);
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const year = date.getFullYear();
                              return `${month}-${day}-${year}`;
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ pt: 0, px: 2, pb: 1 }}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewFile(file)}
                      >
                        {t('common.view', 'View')}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFile(file)}
                      >
                        {t('common.download', 'Download')}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteFile(file)}
                        color="error"
                      >
                        {t('common.delete', 'Delete')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
      )}

      {/* Generated Bills */}
      {mainTab === 2 && (
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">{t('navigation.bills', 'Bills')}</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                if (currentCase.status !== 'completed') {
                  setSnackbar({
                    open: true,
                    message: t('billing.billGenerationRequiresCompletedCase', 'Bill generation is only available for completed cases. Please update the case status to completed first.'),
                    severity: "error",
                  });
                } else {
                  setOpenBillDialog(true);
                }
              }}
            >
              {t('billing.generateBill', 'Generate Bill')}
            </Button>
          </Box>

          {bills?.filter(bill => bill.case_id === parseInt(id!)).length === 0 ? (
            <Typography color="textSecondary">
              {t('billing.noBillsGeneratedYet', 'No bills generated yet')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('billing.billNumber', 'Bill Number')}</TableCell>
                    <TableCell>{t('common.date', 'Date')}</TableCell>
                    <TableCell>{t('billing.amount', 'Amount')}</TableCell>
                    <TableCell>{t('common.status', 'Status')}</TableCell>
                    <TableCell>{t('common.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills
                    ?.filter(bill => bill.case_id === parseInt(id!))
                    .map((bill: Bill) => (
                      <TableRow key={bill.id}>
                        <TableCell>{bill.bill_number}</TableCell>
                        <TableCell>
                          {new Date(bill.issued_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {getCurrencySymbol(bill.currency)}
                          {bill.total}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bill.status}
                            size="small"
                            sx={{
                              backgroundColor: getBillStatusColor(bill.status),
                              color: "white",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {bill.status === "draft" && (
                              <Button
                                size="small"
                                startIcon={<EmailIcon />}
                                onClick={() => handleSendBill(bill.id)}
                              >
                                {t('common.send', 'Send')}
                              </Button>
                            )}
                            {bill.status === "sent" && (
                              <Button
                                size="small"
                                color="success"
                                onClick={() => handleMarkAsPaid(bill.id)}
                              >
                                {t('billing.markAsPaid', 'Mark as Paid')}
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add Activity Log Dialog */}
      <Dialog
        open={openLogDialog}
        onClose={() => setOpenLogDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cases.newEvent', 'New Event')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label={t('cases.activityTitle', 'Activity Title')}
              fullWidth
              value={newLog.title}
              onChange={(e) => setNewLog({ ...newLog, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <RichTextEditor
              label={t('cases.eventDescription', 'Event Description')}
              value={newLog.activity_description}
              onChange={(value) => setNewLog({ ...newLog, activity_description: value })}
              minHeight={110}
            />
            <TextField
              select
              label={t('cases.lawyerStaffMember', 'Lawyer/Staff Member')}
              fullWidth
              value={newLog.lawyer_name}
              onChange={(e) =>
                setNewLog({ ...newLog, lawyer_name: e.target.value })
              }
              sx={{ mb: 2 }}
            >
              {users
                .filter(
                  (userItem) =>
                    userItem.status === "active" &&
                    userItem.firm_id === user?.firm_id,
                )
                .map((userItem) => (
                  <MenuItem key={userItem.id} value={userItem.full_name}>
                    {userItem.full_name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label={t('cases.timeSpent', 'Time Spent (HRS)')}
              type="number"
              fullWidth
              value={newLog.time_spent}
              onChange={(e) =>
                setNewLog({ ...newLog, time_spent: e.target.value })
              }
              inputProps={{ step: 0.5, min: 0 }}
              required={currentCase?.billing_type !== 'fixed'}
              helperText={currentCase?.billing_type === 'fixed' ? t('cases.optionalForFixedBilling', 'Optional for fixed billing cases') : ''}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              label={t('common.status', 'Status')}
              fullWidth
              value={newLog.status}
              onChange={(e) => setNewLog({ ...newLog, status: e.target.value })}
            >
              {eventStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleAddEvent} variant="contained">
            {t('cases.newEvent', 'New Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Activity Log Dialog */}
      <Dialog
        open={editLogDialogOpen}
        onClose={() => {
          setEditLogDialogOpen(false);
          setEditingLog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cases.editEvent', 'Edit Event')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label={t('cases.activityTitle', 'Activity Title')}
              fullWidth
              value={editLogData.title}
              onChange={(e) => setEditLogData({ ...editLogData, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <RichTextEditor
              label={t('cases.eventDescription', 'Event Description')}
              value={editLogData.activity_description}
              onChange={(value) => setEditLogData({ ...editLogData, activity_description: value })}
              minHeight={110}
            />
            <TextField
              select
              label={t('cases.lawyerStaffMember', 'Lawyer/Staff Member')}
              fullWidth
              value={editLogData.lawyer_name}
              onChange={(e) =>
                setEditLogData({ ...editLogData, lawyer_name: e.target.value })
              }
              sx={{ mb: 2 }}
            >
              {users
                .filter(
                  (userItem) =>
                    userItem.status === "active" &&
                    userItem.firm_id === user?.firm_id,
                )
                .map((userItem) => (
                  <MenuItem key={userItem.id} value={userItem.full_name}>
                    {userItem.full_name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label={t('cases.timeSpent', 'Time Spent (HRS)')}
              type="number"
              fullWidth
              value={editLogData.time_spent}
              onChange={(e) =>
                setEditLogData({ ...editLogData, time_spent: e.target.value })
              }
              inputProps={{ step: 0.5, min: 0 }}
              required={currentCase?.billing_type !== 'fixed'}
              helperText={currentCase?.billing_type === 'fixed' ? t('cases.optionalForFixedBilling', 'Optional for fixed billing cases') : ''}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              label={t('common.status', 'Status')}
              fullWidth
              value={editLogData.status}
              onChange={(e) => setEditLogData({ ...editLogData, status: e.target.value })}
            >
              {eventStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditLogDialogOpen(false);
              setEditingLog(null);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleUpdateLog} variant="contained">
            {t('common.saveChanges', 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Activity Log Confirmation Dialog */}
      <Dialog
        open={deleteLogDialogOpen}
        onClose={() => {
          setDeleteLogDialogOpen(false);
          setLogToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('cases.deleteEvent', 'Delete Event')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cases.confirmDeleteEvent', 'Are you sure you want to delete this event? This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteLogDialogOpen(false);
              setLogToDelete(null);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleDeleteLog} color="error" variant="contained">
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Bill Dialog */}
      <Dialog
        open={openBillDialog}
        onClose={() => setOpenBillDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('billing.generateBill', 'Generate Bill')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('billing.generateBillInfo', 'This will generate an invoice based on all activity logs for this case.')}
            </Alert>

            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('billing.billSummary', 'Bill Summary')}
            </Typography>

            {currentCase.billing_type === "fixed" ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('cases.fixedFeeFor', 'Fixed Fee for {{subject}}', { subject: currentCase.subject })}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {getCurrencySymbol(currentCase?.currency || "USD")}
                    {(currentCase?.fixed_fee || currentCase?.total_charges)}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                {/* Calculate and display hourly billing details */}
                {(() => {
                  if (!logs || logs.length === 0) {
                    return <Typography variant="body2">{t('cases.noEventsAvailable', 'No events available')}</Typography>;
                  }

                  const hoursByLawyer = logs?.reduce((acc: any, log: any) => {
                    // Handle different lawyer data structures
                    let lawyerName = 'Unknown';
                    if (log?.lawyer?.full_name) {
                      lawyerName = log.lawyer.full_name;
                    } else if (log?.lawyer_name) {
                      lawyerName = log.lawyer_name;
                    } else if (typeof log?.lawyer === 'string') {
                      lawyerName = log.lawyer;
                    }
                    
                    if (!acc[lawyerName]) {
                      acc[lawyerName] = 0;
                    }
                    
                    // Ensure time_spent is a valid number
                    const timeSpent = parseFloat(log.time_spent) || 0;
                    acc[lawyerName] += timeSpent;
                    return acc;
                  }, {});

                  let billItems = [];
                  let total = 0;

                  // Add supervisor charges if applicable
                  if (currentCase?.supervisor && currentCase.supervisor_fee > 0) {
                    // Handle different supervisor data structures
                    const supervisorName = currentCase.supervisor.full_name || currentCase.supervisor?.name || currentCase.supervisor;
                    const supervisorHours = parseFloat(hoursByLawyer[supervisorName]) || 0;
                    if (supervisorHours > 0) {
                      const supervisorFee = parseFloat(String(currentCase.supervisor_fee)) || 0;
                      const supervisorCharge = supervisorHours * supervisorFee;
                      total += supervisorCharge;
                      billItems.push({
                        description: `Supervisor (${supervisorName}) - ${supervisorHours}h`,
                        quantity: supervisorHours,
                        unit_price: supervisorFee,
                        total: supervisorCharge,
                      });
                    }
                  }

                  // Parse lawyers_assigned to get user_id: fee pairs
                  const lawyerFees: { [key: string]: number } = {};
                  if (currentCase?.lawyers_assigned && typeof currentCase.lawyers_assigned === 'string') {
                    currentCase.lawyers_assigned.split(',').forEach(entry => {
                      const [userId, fee] = entry.trim().split(':').map(s => s.trim());
                      if (userId && fee) {
                        lawyerFees[userId] = parseFloat(fee) || 0;
                      }
                    });
                  }

                  // Add lawyer charges
                  Object.entries(hoursByLawyer).forEach(([lawyerName, hours]) => {
                    // Skip if this is the supervisor (already handled)
                    const supervisorName = currentCase?.supervisor?.full_name || currentCase?.supervisor?.name || currentCase?.supervisor;
                    if (supervisorName === lawyerName) return;
                    
                    // Find the lawyer's user_id from lawyers_list to get their fee
                    const lawyerInfo = currentCase?.lawyers_list?.find((lawyer: any) => lawyer.full_name === lawyerName);
                    const lawyerRate = lawyerInfo ? lawyerFees[lawyerInfo.user_id] || 250 : 250;
                    
                    const hoursNum = parseFloat(String(hours)) || 0;
                    if (hoursNum > 0) {
                      const lawyerCharge = hoursNum * lawyerRate;
                      total += lawyerCharge;
                      billItems.push({
                        description: `${lawyerName} - ${hoursNum}h @ ${getCurrencySymbol(currentCase?.currency || "USD")}${lawyerRate}/h`,
                        quantity: hoursNum,
                        unit_price: lawyerRate,
                        total: lawyerCharge,
                      });
                    }
                  });

                  return <>{billItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
                    >
                      <Typography variant="body2">
                        {item.description}
                      </Typography>
                      <Typography variant="body2">
                        {getCurrencySymbol(currentCase?.currency || "USD")}
                        {item.total.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}</>;
                })()}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6">{t("billing.totalAmount")}</Typography>
              <Typography variant="h6">
                {getCurrencySymbol(currentCase?.currency || "USD")}
                {(() => {
                  if (currentCase.billing_type === "fixed") {
                    return (currentCase?.fixed_fee || currentCase?.total_charges);
                  } else {
                    // Calculate hourly total
                    if (!logs || logs.length === 0) {
                      return "0.00";
                    }

                    const hoursByLawyer = logs?.reduce((acc: any, log: any) => {
                      // Handle different lawyer data structures
                      let lawyerName = 'Unknown';
                      if (log?.lawyer?.full_name) {
                        lawyerName = log.lawyer.full_name;
                      } else if (log?.lawyer_name) {
                        lawyerName = log.lawyer_name;
                      } else if (typeof log?.lawyer === 'string') {
                        lawyerName = log.lawyer;
                      }
                      
                      if (!acc[lawyerName]) {
                        acc[lawyerName] = 0;
                      }
                      
                      // Ensure time_spent is a valid number
                      const timeSpent = parseFloat(log.time_spent) || 0;
                      acc[lawyerName] += timeSpent;
                      return acc;
                    }, {});

                    let total = 0;
                    let billItems = [];

                    // Add supervisor charges
                    if (currentCase?.supervisor && currentCase.supervisor_fee > 0) {
                      // Handle different supervisor data structures
                      const supervisorName = currentCase.supervisor.full_name || currentCase.supervisor.name || currentCase.supervisor;
                      const supervisorHours = parseFloat(hoursByLawyer[supervisorName]) || 0;
                      if (supervisorHours > 0) {
                        const supervisorFee = parseFloat(String(currentCase.supervisor_fee)) || 0;
                        const supervisorCharge = supervisorHours * supervisorFee;
                        total += supervisorCharge;
                        billItems.push({
                          description: `Supervisor (${supervisorName}) - ${supervisorHours}h`,
                          quantity: supervisorHours,
                          unit_price: supervisorFee,
                          total: supervisorCharge,
                        });
                      }
                    }

                    // Parse lawyers_assigned to get user_id: fee pairs
                    const lawyerFees: { [key: string]: number } = {};
                    if (currentCase?.lawyers_assigned && typeof currentCase.lawyers_assigned === 'string') {
                      currentCase.lawyers_assigned.split(',').forEach(entry => {
                        const [userId, fee] = entry.trim().split(':').map(s => s.trim());
                        if (userId && fee) {
                          lawyerFees[userId] = parseFloat(fee) || 0;
                        }
                      });
                    }

                    // Add lawyer charges
                    Object.entries(hoursByLawyer).forEach(([lawyerName, hours]) => {
                      // Skip if this is the supervisor (already handled)
                      const supervisorName = currentCase?.supervisor?.full_name || currentCase?.supervisor?.name || currentCase?.supervisor;
                      if (supervisorName === lawyerName) return;
                      
                      // Find the lawyer's user_id from lawyers_list to get their fee
                      const lawyerInfo = currentCase?.lawyers_list?.find((lawyer: any) => lawyer.full_name === lawyerName);
                      const lawyerRate = lawyerInfo ? lawyerFees[lawyerInfo.user_id] || 250 : 250;
                      const hoursNum = parseFloat(String(hours)) || 0;
                      const lawyerCharge = hoursNum * lawyerRate;
                      total += lawyerCharge;
                      billItems.push({
                        description: `${lawyerName} - ${hoursNum}h @ ${getCurrencySymbol(currentCase?.currency || "USD")}${lawyerRate}/h`,
                        quantity: hoursNum,
                        unit_price: lawyerRate,
                        total: lawyerCharge,
                      });
                    });

                    return total.toFixed(2);
                  }
                })()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBillDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateBill} variant="contained">
            {t("billing.generateBill")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Case Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Edit Case
          <IconButton onClick={handleCloseEditDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={editDialogTab}
          onChange={(e, newValue) => setEditDialogTab(newValue)}
          sx={{ px: 2 }}
        >
          <Tab label={t("cases.caseDetails")} />
        </Tabs>
        <DialogContent dividers>
          {editDialogTab === 0 && (
            <Stack spacing={2}>
              <TextField
                select
                label={t("cases.client")}
                required
                fullWidth
                value={editFormData.customer_id}
                onChange={(e) => {
                  setEditFormData({
                    ...editFormData,
                    customer_id: e.target.value,
                  });
                  if (editFormErrors.customer_id) {
                    setEditFormErrors((prev) => ({ ...prev, customer_id: "" }));
                  }
                }}
                error={!!editFormErrors.customer_id}
                helperText={editFormErrors.customer_id}
              >
                {clients?.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {`${client.first_name} ${client.last_name}`}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t("cases.caseType")}
                fullWidth
                required
                value={editFormData.type}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, type: e.target.value })
                }
              >
                <MenuItem value="Arbitration">{t("cases.typeArbitration")}</MenuItem>
                <MenuItem value="Banking and Finance">
                  {t("cases.typeBankingAndFinance")}
                </MenuItem>
                <MenuItem value="Project Finance">{t("cases.typeProjectFinance")}</MenuItem>
                <MenuItem value="Capital Markets">{t("cases.typeCapitalMarkets")}</MenuItem>
                <MenuItem value="Competition and Antitrust">
                  {t("cases.typeCompetitionAndAntitrust")}
                </MenuItem>
                <MenuItem value="Construction Law">{t("cases.typeConstructionLaw")}</MenuItem>
                <MenuItem value="Corporate Law">{t("cases.typeCorporateLaw")}</MenuItem>
                <MenuItem value="Corporate Governance">
                  {t("cases.typeCorporateGovernance")}
                </MenuItem>
                <MenuItem value="Employment">{t("cases.typeEmployment")}</MenuItem>
                <MenuItem value="Oil, Gas and Mining">
                  {t("cases.typeOilGasAndMining")}
                </MenuItem>
                <MenuItem value="Energy">{t("cases.typeEnergy")}</MenuItem>
                <MenuItem value="Franchising">{t("cases.typeFranchising")}</MenuItem>
                <MenuItem value="Immigration">{t("cases.typeImmigration")}</MenuItem>
                <MenuItem value="Intellectual Property and Technology">
                  {t("cases.typeIntellectualPropertyAndTechnology")}
                </MenuItem>
                <MenuItem value="Litigation and Dispute Resolution">
                  {t("cases.typeLitigationAndDisputeResolution")}
                </MenuItem>
                <MenuItem value="Tax">{t("cases.typeTax")}</MenuItem>
                <MenuItem value="Transportation">{t("cases.typeTransportation")}</MenuItem>
              </TextField>

              <TextField
                required
                label={t("cases.subject")}
                fullWidth
                value={editFormData.subject}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, subject: e.target.value })
                }
                error={!!editFormErrors.subject}
                helperText={editFormErrors.subject}
              />

              <TextField
                select
                label={t("cases.billingType")}
                fullWidth
                required
                value={editFormData.billing_type}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, billing_type: e.target.value })
                }
              >
                <MenuItem value="hourly">{t("cases.hourly")}</MenuItem>
                <MenuItem value="fixed">{t("cases.fixed")}</MenuItem>
              </TextField>

              {/* Supervisor and Lawyers Assignment - Available for All Billing Types */}
              
              <TextField
                select
                label={t("cases.currency")}
                fullWidth
                value={editFormData.currency}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, currency: e.target.value })
                }
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
                <MenuItem value="XOF">XOF</MenuItem>
              </TextField>

              <TextField
                select
                label={t("cases.priority")}
                fullWidth
                value={editFormData.priority}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, priority: e.target.value })
                }
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">{t("common.medium")}</MenuItem>
                <MenuItem value="high">{t("common.high")}</MenuItem>
              </TextField>

              {/* Supervisor and Supervisor Fee - Only for Hourly Billing */}
              {editFormData.billing_type === "hourly" && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    label={t("cases.supervisor")}
                    fullWidth
                    required
                    value={editFormData.supervisor}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, supervisor: e.target.value })
                    }
                  >
                    {users
                      .filter(
                        (userItem) =>
                          userItem.status === "active" &&
                          userItem.firm_id === user?.firm_id &&
                          userItem.role?.name !== "client"
                      )
                      .map((userItem) => (
                        <MenuItem key={userItem.id} value={userItem.user_id}>
                          {userItem.full_name}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    label={t("cases.supervisorFee")}
                    type="number"
                    fullWidth
                    value={editFormData.supervisor_fee}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, supervisor_fee: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(editFormData.currency)}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {/* Lawyers Assignment - Available for All Billing Types */}
              {/* For Fixed billing: show lawyers but without fees */}
              {/* For Hourly billing: show lawyers with fees */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  Lawyers Assigned
                </Typography>
                {editFormData.lawyers_assigned.map((lawyer, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      label={t("cases.lawyer")}
                      fullWidth
                      value={lawyer.user_id}
                      onChange={(e) => {
                        const updatedLawyers = [...editFormData.lawyers_assigned];
                        updatedLawyers[index] = { ...lawyer, user_id: e.target.value };
                        setEditFormData({ ...editFormData, lawyers_assigned: updatedLawyers });
                      }}
                    >
                      {users
                        .filter(
                          (userItem) =>
                            userItem.status === "active" &&
                            userItem.firm_id === user?.firm_id &&
                            userItem.role?.name !== "client"
                        )
                        .map((userItem) => (
                          <MenuItem key={userItem.id} value={userItem.user_id}>
                            {userItem.full_name}
                          </MenuItem>
                        ))}
                    </TextField>

                    {editFormData.billing_type === "hourly" && (
                      <TextField
                        label={t("cases.fee")}
                        type="number"
                        fullWidth
                        value={lawyer.fee}
                        onChange={(e) => {
                          const updatedLawyers = [...editFormData.lawyers_assigned];
                          updatedLawyers[index] = { ...lawyer, fee: e.target.value };
                          setEditFormData({ ...editFormData, lawyers_assigned: updatedLawyers });
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {getCurrencySymbol(editFormData.currency)}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}

                    <IconButton
                      onClick={() => {
                        const updatedLawyers = editFormData.lawyers_assigned.filter((_, i) => i !== index);
                        setEditFormData({ ...editFormData, lawyers_assigned: updatedLawyers });
                      }}
                      color="error"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditFormData({
                      ...editFormData,
                      lawyers_assigned: [
                        ...editFormData.lawyers_assigned,
                        { user_id: "", fee: editFormData.billing_type === "hourly" ? "250" : "" },
                      ],
                    });
                  }}
                >
                  Add Lawyer
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{t("common.cancel")}</Button>
          <Button onClick={handleUpdateCase} variant="contained">
            {t("cases.updateCase")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("cases.updateStatus")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("cases.selectNewStatus")}
          </Typography>
          <TextField
            select
            fullWidth
            label={t("common.status")}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{ mb: 2 }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: option.color,
                    }}
                  />
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            {t("cases.updateStatus")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* File Upload Confirmation Dialog */}
      <Dialog
        open={confirmUploadDialog}
        onClose={cancelFileUpload}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("cases.confirmFileUploadTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("cases.confirmUploadFilesQuestion")}
          </Typography>
          {selectedFiles && (
            <List>
              {Array.from(selectedFiles).map((file, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={file.name}
                    secondary={`${caseFilesService.formatFileSize(file.size)}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelFileUpload} disabled={uploading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirmFileUpload}
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? t("common.uploading") : t("cases.confirmUpload")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseDetails;
